import React from 'react';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

/**
 * ResponsiveTabs — barre d'onglets responsive.
 *
 * Deux modes :
 *  - standalone (défaut) : wrappe lui-même dans <Tabs>. À utiliser quand on n'a pas
 *    de <TabsContent> à afficher (navigation pure). Ex. AdminDashboard.
 *  - inline (asTabsListReplacement=true) : ne wrappe pas. À placer à l'intérieur
 *    d'un <Tabs> parent qui contient aussi les <TabsContent>. Ex. ReferentDashboard.
 *
 * Props :
 *  - tabs : Array<{ value: string, label: string, icon?: Component }>
 *  - value : string
 *  - onValueChange : (value: string) => void
 *  - asTabsListReplacement : bool (voir ci-dessus)
 *  - className, tabClassName, listClassName : classes Tailwind
 *  - mobilePlaceholder : string
 */
const ResponsiveTabs = ({
  tabs,
  value,
  onValueChange,
  asTabsListReplacement = false,
  className = '',
  tabClassName = 'rounded-lg px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm',
  listClassName = 'bg-slate-100 p-1 rounded-xl',
  mobilePlaceholder = 'Choisir une section',
}) => {
  const activeTab = tabs.find((t) => t.value === value);

  const desktopList = (
    <TabsList className={`${listClassName} flex-wrap h-auto`}>
      {tabs.map((tab) => {
        const Icon = tab.icon;
        return (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            className={tabClassName}
            data-testid={`tab-${tab.value}`}
          >
            {Icon && <Icon className="w-4 h-4 mr-2" />}
            {tab.label}
            {tab.badge && <span className="ml-2">{tab.badge}</span>}
          </TabsTrigger>
        );
      })}
    </TabsList>
  );

  const mobileSelect = (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className="w-full h-11 rounded-xl bg-slate-100 border-slate-200 text-sm font-medium"
        data-testid="mobile-tabs-select"
      >
        <SelectValue placeholder={mobilePlaceholder}>
          {activeTab ? (
            <span className="flex items-center">
              {activeTab.icon && <activeTab.icon className="w-4 h-4 mr-2" />}
              {activeTab.label}
            </span>
          ) : mobilePlaceholder}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-[70vh]">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <SelectItem
              key={tab.value}
              value={tab.value}
              data-testid={`mobile-tab-${tab.value}`}
            >
              <span className="flex items-center">
                {Icon && <Icon className="w-4 h-4 mr-2" />}
                {tab.label}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );

  if (asTabsListReplacement) {
    return (
      <div className={className}>
        <div className="hidden md:block">{desktopList}</div>
        <div className="md:hidden mb-4">{mobileSelect}</div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="hidden md:block">
        <Tabs value={value} onValueChange={onValueChange}>
          {desktopList}
        </Tabs>
      </div>
      <div className="md:hidden">{mobileSelect}</div>
    </div>
  );
};

export default ResponsiveTabs;
